/**
 * `refetchInterval` factory for status-polling queries.
 *
 * The app-wide QueryClient runs with `retry: false`, so a single transient
 * fetch error (network blip, 5xx, a 429 from the status endpoint's 2s rate
 * limit) immediately lands in `query.state.error`. A naive interval callback
 * that stops on error therefore latches polling off forever — the UI freezes
 * on "analyzing" while the work completes server-side.
 *
 * This factory keeps polling through transient errors at a slower backoff
 * cadence (default 6s — 3× the backend's 2s status-poll rate-limit window,
 * so a second observer of the same task can't starve us into permanent 429s)
 * and gives up only once the query has failed `maxConsecutiveErrors` times
 * without ever returning data.
 *
 * NOTE: we deliberately do NOT use `fetchFailureCount` / `failureCount` here.
 * React Query resets those to 0 at the start of every fetch, and with the
 * app-wide `retry: false` each poll cycle is a single attempt, so they only
 * ever oscillate 0↔1 and never reach the threshold — the give-up guard would
 * be dead code. Instead we use the monotonic `errorUpdateCount` together with
 * `dataUpdatedAt` (0 until the first successful response), which survive across
 * poll cycles. This covers the dominant failure mode — a status endpoint that
 * fails from the start (deleted batch → 404, backend down → 5xx). A query that
 * succeeds first and only then fails permanently keeps polling; that is an
 * accepted limitation, not a regression.
 *
 * Callers should pair it with `meta: { suppressErrorToast: true }` (the global
 * query cache toasts every failed fetch otherwise) and surface
 * `isPollingStalled` in the UI when polling has given up.
 */
export interface PollingRefetchIntervalOptions<TData> {
	/** True when the watched process reached a terminal state — stop polling. */
	isTerminal: (data: TData | undefined) => boolean;
	intervalMs?: number;
	errorBackoffMs?: number;
	maxConsecutiveErrors?: number;
}

export const POLLING_MAX_CONSECUTIVE_ERRORS = 5;

/**
 * True when a query has errored `maxConsecutiveErrors` times without ever
 * returning data. Reads `errorUpdateCount` / `dataUpdatedAt`, both of which are
 * present on a React Query `QueryState` and on the `useQuery` result, so the
 * same predicate backs the refetch-interval guard and the UI stalled flag.
 */
export const isPollingStalled = (
	query: { dataUpdatedAt: number; errorUpdateCount: number },
	maxConsecutiveErrors = POLLING_MAX_CONSECUTIVE_ERRORS
): boolean => query.dataUpdatedAt === 0 && query.errorUpdateCount >= maxConsecutiveErrors;

export const createPollingRefetchInterval =
	<TData>({
		isTerminal,
		intervalMs = 3000,
		errorBackoffMs = 6000,
		maxConsecutiveErrors = POLLING_MAX_CONSECUTIVE_ERRORS,
	}: PollingRefetchIntervalOptions<TData>) =>
	(query: {
		state: { data?: TData; error: unknown; dataUpdatedAt: number; errorUpdateCount: number };
	}): number | false => {
		if (isTerminal(query.state.data)) {
			return false;
		}
		if (isPollingStalled(query.state, maxConsecutiveErrors)) {
			// Persistent failure — stop hammering the endpoint. The caller
			// should render a "live updates unavailable" notice off
			// isPollingStalled instead of silently freezing.
			return false;
		}
		if (query.state.error) {
			return errorBackoffMs;
		}
		return intervalMs;
	};
